import React, { createRef, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { PlotViewport } from '../contract'
import { usePlotGraphicBudget } from '../_shared/usePlotGraphicBudget'

import { TextClamp } from '../../components/text/TextClamp'
import { ChartLabel } from '../_shared/ChartLabel'
import { ChartLabelFullText, ChartLabelRenderMode } from '../_shared/ChartLabelFullText'
import { ShareUnit } from '../_shared/ShareUnitToggle'
import { PlotPropsOf } from '../contract'
import {
  DensityFormatter,
  DensityHistogramData,
  DensityHistogramPlotLabels,
  densityFormatter,
  fillDensityTemplate,
  formatDensityShare,
} from './format'
import { DensityView, DensityViewBin, DensityViewSeries, densityShareAxis, densityValueToX, densityView } from './view'

/**
 * DensityHistogramPlot: the `density-histogram` kind (plots plan M).
 *
 * One histogram per series on the SHARED bin edges Python stamped (Total, a
 * lane, a saved segment or a quick-study item), share of respondents on the
 * y axis, the median as a dashed line with its note above the plot, and the
 * middle-50% band only where Python allowed it (never below THIN_N).
 *
 * - `size: 'slide'`: the deck panel's chart (`ContinuousDensitySlide`), the
 *   geometry moved unchanged; the slide owns the panel, its title and the
 *   segment control.
 * - `size: 'report'` (and `card`): the survey tab, quick study and cards;
 *   responsive DOM, bin names wrap under their bar, the median / middle 50% /
 *   average tiles above.
 *
 * Every word is a label key (`deck_labels.py`, or the app's translation of
 * the same key); a label the payload lacks is not drawn. Every figure is the
 * payload's; the plot only positions it.
 */

export type DensityHistogramPlotData = Omit<DensityHistogramData, 'labels'> & { labels: DensityHistogramPlotLabels }

export interface DensityHistogramPlotProps {
  viewport?: PlotViewport
  data: DensityHistogramPlotData
  props?: PlotPropsOf<'density-histogram'>
  /** The study's chart labels (`chartLabels`), looked up by `measure.sourceId`. */
  chartLabels?: Record<string, string> | null
  /** Overrides the payload formatter (the deck's legacy reader for stored slides). */
  formatter?: DensityFormatter
  /** A resolved view (the slide builds it once for its stat row and segment columns). */
  view?: DensityView
  testIdPrefix?: string
  /** Survey tab: print each bin's stamped count. Default percent. Bar heights stay shares. */
  shareUnit?: ShareUnit
}

/** The view a surface draws, with the payload formatter unless one is given. */
export function resolveDensityView(
  data: DensityHistogramPlotData,
  locale: string,
  chartLabels?: Record<string, string> | null,
  formatter?: DensityFormatter,
): DensityView {
  return densityView(data, formatter ?? densityFormatter(data.measure, data.labels, locale), chartLabels)
}

function binFigure(bin: DensityViewBin, shareUnit: ShareUnit, locale: string): string {
  if (shareUnit === 'count' && typeof bin.count === 'number') {
    return new Intl.NumberFormat(locale || undefined, { maximumFractionDigits: 0 }).format(bin.count)
  }
  return formatDensityShare(bin.share, locale)
}

export function DensityHistogramPlot(props: DensityHistogramPlotProps) {
  const { data, props: renderProps, chartLabels, formatter, testIdPrefix = 'density-histogram', shareUnit = 'percent' } = props

  const locale = renderProps?.locale || 'en'
  const view = props.view ?? resolveDensityView(data, locale, chartLabels, formatter)

  if (renderProps?.size === 'slide') {
    return <DensityHistogramSlideChart view={view} />
  }
  return (
    <DensityHistogramReport
      viewport={props.viewport}
      view={view}
      labels={data.labels}
      locale={locale}
      showBand={renderProps?.showBand !== false}
      showMedian={renderProps?.showMedian !== false}
      showStats={renderProps?.showStats !== false}
      maxSeries={renderProps?.maxSeries}
      shareUnit={shareUnit}
      testIdPrefix={testIdPrefix}
    />
  )
}

// -- Slide (deck panel) ------------------------------------------------------------

const LINE = 'var(--slide-border-soft)'
const GRAY = 'var(--slide-text-muted)'
const BLACK = 'var(--slide-text-primary)'
const PLOT_COLOR = 'var(--slide-chart-1)'
/** P0-1: the band reads as annotation (a low-opacity neutral wash), never as a bar. */
const BAND_FILL = 'var(--slide-neutral-mid, #D7D7D7)'
const BAND_FILL_OPACITY = 0.38
const MARKER = '#BDBDBD'
const BAND_EDGE = GRAY

/**
 * The deck panel's plot box. Its top leaves the question heading above it the three
 * lines its schema budget grants (`ContinuousDensitySlide`); the bottom is unchanged,
 * so the bars gave up 20px of height, not the axis below them. NA-09's 270-character
 * question beside the middle-50% key needed three lines (2026-10-14).
 */
export const DENSITY_SLIDE_PLOT = {
  left: 68,
  top: 118,
  width: 1222,
  height: 240,
}
const PLOT = DENSITY_SLIDE_PLOT
/** The row above the bars that carries the y-axis title and the median label. */
export const DENSITY_SLIDE_LABEL_ROW_TOP = PLOT.top - 20
const X_LABEL_TOP = PLOT.top + PLOT.height + 8
const X_TITLE_TOP = X_LABEL_TOP + 18
const BAR_GAP = 6
/** P1-4: the overflow tail's extra leading gap, so it reads as a compressed tail. */
const OVERFLOW_GAP = 14
const BAND_LEGEND_SWATCH_SIZE = 10

function clamp(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value))
}

function bandLegendText(view: DensityView, band: { low: number; high: number }): string {
  const range = view.formatter.range(band.low, band.high)
  if (range == null) {
    return view.bandWord ?? ''
  }
  return view.bandWord ? `${view.bandWord}: ${range}` : range
}

function medianNoteText(view: DensityView, median: number): string {
  const value = view.formatter.annotation(median)
  return view.medianWord ? `${view.medianWord}: ${value}` : value
}

/**
 * The middle-50% key. It sits in the slide's chart header beside the question
 * (`ContinuousDensitySlide`, the shared `ChartSegmentTitleSlot` row every chart
 * slide uses), so the question gives it room and neither reaches the segment
 * control. Drawn at a fixed top-right offset it printed over a two-line
 * question and under the control (NA-09 s08/s09).
 */
export function DensityBandLegend({ view }: { view: DensityView }) {
  const band = view.series[0]?.band ?? null
  if (!band) {
    return null
  }
  return (
    <div
      data-testid="continuous-density-band-legend"
      style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
    >
      <span
        aria-hidden="true"
        data-testid="continuous-density-band-legend-swatch"
        style={{
          flexShrink: 0,
          width: BAND_LEGEND_SWATCH_SIZE,
          height: BAND_LEGEND_SWATCH_SIZE,
          borderRadius: 2,
          border: `1px solid ${BAND_EDGE}`,
          overflow: 'hidden',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            background: BAND_FILL,
            opacity: BAND_FILL_OPACITY,
          }}
        />
      </span>
      <span style={{ fontSize: 11, lineHeight: 1.2, fontWeight: 600, color: GRAY }}>
        {bandLegendText(view, band)}
      </span>
    </div>
  )
}

export interface DensityHistogramSlideChartProps {
  view: DensityView
  /** Plots plan LB: a static render (PDF) keeps the ellipsis and draws no (i). */
  renderMode?: ChartLabelRenderMode
}

/**
 * The deck panel's chart. Exported for `ContinuousDensitySlide`, which owns the
 * panel, the chart title and the segment control around it.
 */
export function DensityHistogramSlideChart(props: DensityHistogramSlideChartProps) {
  const { view, renderMode } = props

  const overflowHatchId = `continuous-density-overflow-hatch-${React.useId()}`

  const series: DensityViewSeries | undefined = view.series[0]
  const bins: Array<DensityViewBin> = series?.bins ?? []
  const regular = bins.filter((bin) => !bin.isOverflow)
  const slotWidth = bins.length ? PLOT.width / bins.length : 0
  const maxBarValue = bins.length ? Math.max(...bins.map((bin) => bin.share)) : 0
  const { ceiling, step: tickStep } = densityShareAxis(maxBarValue)
  const yFor = (value: number) => PLOT.height - clamp(ceiling > 0 ? value / ceiling : 0) * PLOT.height
  const tickCount = Math.max(1, Math.round(ceiling / tickStep))
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) => ceiling - i * tickStep)
  const band = series?.band ?? null
  const bandX1 = band ? densityValueToX(band.low, regular, slotWidth, BAR_GAP) : 0
  const bandX2 = band ? densityValueToX(band.high, regular, slotWidth, BAR_GAP) : 0
  const bandWidth = band ? Math.max(0, bandX2 - bandX1) : 0
  const median = series?.median ?? null
  const medianX = median != null && regular.length ? densityValueToX(median, regular, slotWidth, BAR_GAP) : null

  return (
    <>
      <TextClamp data-testid="continuous-density-y-axis-title" minLines={1} maxLines={1} lineHeight="1.2" style={{ position: 'absolute', left: 31, top: DENSITY_SLIDE_LABEL_ROW_TOP, fontSize: 11, color: GRAY }}>
        {view.yAxisTitle ?? ''}
      </TextClamp>

      {yTicks.map((tick, index) => {
        const y = PLOT.top + yFor(tick)
        return (
          <React.Fragment key={tick}>
            <TextClamp
              data-testid={index === 0 ? 'continuous-density-y-axis-ceiling' : undefined}
              minLines={1}
              maxLines={1}
              lineHeight="1.2"
              style={{ position: 'absolute', left: 31, top: y - 6, width: 32, fontSize: 10, fontWeight: 500, color: GRAY }}
            >
              {`${Math.round(tick)}%`}
            </TextClamp>
            <div style={{ position: 'absolute', left: PLOT.left, top: y, width: PLOT.width, height: 1, borderTop: `1px dashed ${LINE}` }} />
          </React.Fragment>
        )
      })}

      <svg data-testid="continuous-density-chart" viewBox={`0 0 ${PLOT.width} ${PLOT.height}`} preserveAspectRatio="none" style={{ position: 'absolute', left: PLOT.left, top: PLOT.top, width: PLOT.width, height: PLOT.height, overflow: 'visible' }}>
        <defs>
          {/* P1-4: the overflow bar is told apart by texture, not hue, so it reads in greyscale. */}
          <pattern id={overflowHatchId} patternUnits="userSpaceOnUse" width={8} height={8} patternTransform="rotate(45)">
            <rect width={8} height={8} fill={PLOT_COLOR} opacity={0.35} />
            <line x1={0} y1={0} x2={0} y2={8} stroke="#FFFFFF" strokeWidth={3} />
          </pattern>
        </defs>

        {band ? (
          <rect
            data-testid="continuous-density-band"
            x={bandX1}
            y={0}
            width={bandWidth}
            height={PLOT.height}
            fill={BAND_FILL}
            opacity={BAND_FILL_OPACITY}
          />
        ) : null}

        {band ? (
          <>
            <line data-testid="continuous-density-band-edge-p25" x1={bandX1} x2={bandX1} y1={0} y2={PLOT.height} stroke={BAND_EDGE} strokeWidth={1} />
            <line data-testid="continuous-density-band-edge-p75" x1={bandX2} x2={bandX2} y1={0} y2={PLOT.height} stroke={BAND_EDGE} strokeWidth={1} />
          </>
        ) : null}

        {bins.map((bin, index) => {
          const leadingGap = BAR_GAP + (bin.isOverflow ? OVERFLOW_GAP : 0)
          const x = index * slotWidth + leadingGap / 2 + (bin.isOverflow ? OVERFLOW_GAP / 2 : 0)
          const width = Math.max(0, slotWidth - leadingGap)
          const top = yFor(bin.share)
          return (
            <rect
              key={bin.id}
              data-testid="continuous-density-bar"
              data-bin-id={bin.id}
              data-overflow={bin.isOverflow ? 'true' : undefined}
              x={x}
              y={top}
              width={width}
              height={Math.max(0, PLOT.height - top)}
              fill={bin.isOverflow ? `url(#${overflowHatchId})` : PLOT_COLOR}
              stroke={bin.isOverflow ? MARKER : 'none'}
              strokeWidth={bin.isOverflow ? 1 : 0}
            />
          )
        })}

        {medianX != null ? (
          // P2: drawn last with a white halo, so it stays legible over band and bars.
          <>
            <line x1={medianX} x2={medianX} y1={0} y2={PLOT.height} stroke="#FFFFFF" strokeWidth={3} strokeDasharray="5 5" opacity={0.9} />
            <line data-testid="continuous-density-median-line" x1={medianX} x2={medianX} y1={0} y2={PLOT.height} stroke={BLACK} strokeWidth={1.5} strokeDasharray="5 5" />
          </>
        ) : null}
      </svg>

      {medianX != null && median != null ? (
        <TextClamp
          data-testid="continuous-density-median-label"
          minLines={1}
          maxLines={1}
          lineHeight="1.2"
          style={{ position: 'absolute', left: Math.max(PLOT.left, PLOT.left + medianX - 30), top: DENSITY_SLIDE_LABEL_ROW_TOP, width: 90, fontSize: 11, fontWeight: 600, color: GRAY }}
        >
          {medianNoteText(view, median)}
        </TextClamp>
      ) : null}

      {bins.map((bin, index) => (
        <TextClamp
          key={bin.id}
          data-testid={bin.isOverflow ? 'continuous-density-bin-label-overflow' : 'continuous-density-bin-label'}
          minLines={1}
          maxLines={1}
          lineHeight="1.4"
          style={{ position: 'absolute', left: PLOT.left + index * slotWidth, top: X_LABEL_TOP, width: slotWidth, textAlign: 'center', fontSize: 10, fontWeight: 500, color: GRAY }}
        >
          {bin.label ?? ''}
        </TextClamp>
      ))}

      {renderMode === 'static' ? (
        <TextClamp data-testid="continuous-density-x-axis-title" minLines={1} maxLines={1} lineHeight="1.2" style={{ position: 'absolute', left: PLOT.left, top: X_TITLE_TOP, width: PLOT.width, textAlign: 'center', fontSize: 11, color: GRAY }}>
          {view.xAxisTitle ?? ''}
        </TextClamp>
      ) : (
        <div style={{ position: 'absolute', left: PLOT.left, top: X_TITLE_TOP, width: PLOT.width, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
          <ChartLabel
            data-testid="continuous-density-x-axis-title"
            text={view.xAxisTitle ?? ''}
            fullText={view.xAxisFullTitle}
            hintIndex={0}
            hintSize={11}
            maxLines={1}
            style={{ textAlign: 'center', fontSize: 11, color: GRAY, minWidth: 0 }}
          />
        </div>
      )}
    </>
  )
}

// -- Report (survey tab, quick study, card) ---------------------------------------------

const REPORT_PLOT_HEIGHT = 180
const REPORT_TICK_COLUMN = 40
const MUTED = 'var(--slide-text-muted, #5b6b67)'
const INK = 'var(--slide-text, #15201e)'
const GRID = 'var(--slide-grid, #dbe3e0)'

interface DensityHistogramReportProps {
  viewport?: PlotViewport
  view: DensityView
  labels: DensityHistogramPlotLabels
  locale: string
  showBand: boolean
  showMedian: boolean
  showStats: boolean
  maxSeries?: number
  shareUnit: ShareUnit
  testIdPrefix: string
}

function DensityHistogramReport(props: DensityHistogramReportProps) {
  const { view, labels, locale, showBand, showMedian, showStats, maxSeries, shareUnit, testIdPrefix } = props

  const shown = typeof maxSeries === 'number' ? view.series.slice(0, Math.max(1, maxSeries)) : view.series
  // One share axis for every small multiple, so heights compare across series.
  const maxShare = Math.max(0, ...shown.flatMap((series) => series.bins.map((bin) => bin.share)))
  const axis = densityShareAxis(maxShare)
  const rootRef = useRef<HTMLDivElement>(null)
  const graphIds = shown.filter((series) => series.bins.length > 0).map((series) => series.id)
  const graphicRefs = useMemo(() => graphIds.map(() => createRef<HTMLDivElement>()), [graphIds.join('\u0000')])
  const tickRef = useRef<HTMLSpanElement>(null)
  const valueRef = useRef<HTMLSpanElement>(null)
  const [minimumHeight, setMinimumHeight] = useState(0)
  const budget = usePlotGraphicBudget(props.viewport, rootRef, graphicRefs)
  useLayoutEffect(() => {
    let active = true
    const measure = () => {
      if (!active || !tickRef.current || !valueRef.current) return
      // Tick centres must remain a full label-height apart; each bar value
      // keeps its existing two-pixel separation from its graphic.
      const minimum = Math.max(Math.round(axis.ceiling / axis.step) * tickRef.current.clientHeight,
        valueRef.current.clientHeight + 2)
      setMinimumHeight((previous) => previous === minimum ? previous : minimum)
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    ;[tickRef.current, valueRef.current].forEach((element) => element && observer?.observe(element))
    void document.fonts?.ready.then(measure)
    return () => { active = false; observer?.disconnect() }
  }, [axis.ceiling, axis.step])
  const graphicHeight = budget ? Math.max(minimumHeight, Math.min(REPORT_PLOT_HEIGHT, budget.height)) : REPORT_PLOT_HEIGHT
  const isMulti = shown.length > 1

  return (
    <div ref={rootRef} data-viewport-infeasible={budget ? budget.height < minimumHeight : undefined} data-testid={testIdPrefix} style={{ display: 'flex', flexDirection: 'column', gap: 20, color: INK, fontSize: 12, width: '100%' }}>
      {shown.map((series, index) => (
        <DensityHistogramReportSeries
          key={series.id}
          graphicRef={graphicRefs[graphIds.indexOf(series.id)]}
          tickRef={series.id === graphIds[0] ? tickRef : undefined}
          valueRef={series.id === graphIds[0] ? valueRef : undefined}
          graphicHeight={graphicHeight}
          bounded={props.viewport !== undefined}
          view={view}
          series={series}
          labels={labels}
          locale={locale}
          axis={axis}
          showHeader={isMulti || series.label != null}
          showBand={showBand}
          showMedian={showMedian}
          showStats={showStats}
          shareUnit={shareUnit}
          testIdPrefix={`${testIdPrefix}-series-${index}`}
        />
      ))}
      {view.xAxisTitle && view.xAxisFullTitle ? (
        <div style={{ textAlign: 'center', fontSize: 11, color: MUTED, marginLeft: REPORT_TICK_COLUMN }}>
          <span data-testid={`${testIdPrefix}-x-axis-title`}>{view.xAxisTitle}</span>
          <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>
            <ChartLabelFullText label={view.xAxisTitle} fullText={view.xAxisFullTitle} index={0} size={11} />
          </span>
        </div>
      ) : view.xAxisTitle ? (
        <div data-testid={`${testIdPrefix}-x-axis-title`} style={{ textAlign: 'center', fontSize: 11, color: MUTED, marginLeft: REPORT_TICK_COLUMN }}>
          {view.xAxisTitle}
        </div>
      ) : null}
    </div>
  )
}

interface DensityHistogramReportSeriesProps {
  graphicRef?: React.Ref<HTMLDivElement>
  tickRef?: React.Ref<HTMLSpanElement>
  valueRef?: React.Ref<HTMLSpanElement>
  graphicHeight: number
  bounded: boolean
  view: DensityView
  series: DensityViewSeries
  labels: DensityHistogramPlotLabels
  locale: string
  axis: { ceiling: number; step: number }
  showHeader: boolean
  showBand: boolean
  showMedian: boolean
  showStats: boolean
  shareUnit: ShareUnit
  testIdPrefix: string
}

function DensityHistogramReportSeries(props: DensityHistogramReportSeriesProps) {
  const { view, series, labels, locale, axis, showHeader, showBand, showMedian, showStats, shareUnit, testIdPrefix } = props

  const bins = series.bins
  const regular = bins.filter((bin) => !bin.isOverflow)
  const slot = bins.length ? 100 / bins.length : 0
  const pctFor = (share: number) => clamp(axis.ceiling > 0 ? share / axis.ceiling : 0) * 100
  const tickCount = Math.max(1, Math.round(axis.ceiling / axis.step))
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => axis.ceiling - i * axis.step)
  const band = showBand ? series.band : null
  const bandLeft = band ? densityValueToX(band.low, regular, slot, 0) : 0
  const bandRight = band ? densityValueToX(band.high, regular, slot, 0) : 0
  const medianLeft = showMedian && series.median != null && regular.length ? densityValueToX(series.median, regular, slot, 0) : null
  const baseText = labels.plot_base_n ? fillDensityTemplate(labels.plot_base_n, { n: series.base }) : null
  const tiles: Array<[string, string, string]> = []
  if (series.median != null && view.medianWord) {
    tiles.push(['median', view.medianWord, view.formatter.median(series.median)])
  }
  const bandRange = series.band ? view.formatter.range(series.band.low, series.band.high) : null
  if (bandRange && view.bandWord) {
    tiles.push(['band', view.bandWord, bandRange])
  }
  if (series.mean != null && view.meanWord) {
    tiles.push(['mean', view.meanWord, view.formatter.value(series.mean)])
  }

  if (!bins.length) {
    return (
      <section data-testid={testIdPrefix} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {showHeader ? <SeriesHeader series={series} baseText={baseText} lowBaseText={labels.plot_low_base} testIdPrefix={testIdPrefix} /> : null}
        <div data-testid={`${testIdPrefix}-empty`} />
      </section>
    )
  }

  return (
    <section data-testid={testIdPrefix} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {showHeader ? <SeriesHeader series={series} baseText={baseText} lowBaseText={labels.plot_low_base} testIdPrefix={testIdPrefix} /> : null}

      {showStats && tiles.length ? (
        <div data-testid={`${testIdPrefix}-stats`} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {tiles.map(([key, word, value]) => (
            <div
              key={key}
              data-testid={`${testIdPrefix}-stat-${key}`}
              style={{ flex: '1 1 120px', minWidth: 0, border: `1px solid ${GRID}`, borderRadius: 8, padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <span style={{ fontSize: 11, color: MUTED }}>{word}</span>
              <span style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            </div>
          ))}
        </div>
      ) : null}

      {view.yAxisTitle ? (
        <div data-testid={`${testIdPrefix}-y-axis-title`} style={{ fontSize: 11, color: MUTED }}>
          {view.yAxisTitle}
        </div>
      ) : null}

      {/* Notes sit above the plot, never on the marks: the median note row. */}
      <div style={{ position: 'relative', height: 16, marginLeft: REPORT_TICK_COLUMN }}>
        {medianLeft != null && series.median != null ? (
          <span
            data-testid={`${testIdPrefix}-median-label`}
            style={{ position: 'absolute', left: `${medianLeft}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 600 }}
          >
            {medianNoteText(view, series.median)}
          </span>
        ) : null}
      </div>

      <div ref={props.graphicRef} style={{ display: 'grid', gridTemplateColumns: `${REPORT_TICK_COLUMN}px minmax(0, 1fr)` }}>
        <div style={{ position: 'relative', height: props.graphicHeight }}>
          {ticks.map((tick) => (
            <span
              ref={tick === ticks[0] ? props.tickRef : undefined}
              key={tick}
              style={{ position: 'absolute', right: 6, top: `${100 - pctFor(tick)}%`, transform: 'translateY(-50%)', fontSize: 10, color: MUTED }}
            >
              {formatDensityShare(Math.round(tick), locale)}
            </span>
          ))}
        </div>
        <div data-testid={`${testIdPrefix}-plot`} style={{ position: 'relative', height: props.graphicHeight }}>
          {ticks.map((tick) => (
            <div key={tick} style={{ position: 'absolute', left: 0, right: 0, top: `${100 - pctFor(tick)}%`, borderTop: `1px dashed ${GRID}` }} />
          ))}
          {band ? (
            <div
              data-testid={`${testIdPrefix}-band`}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${bandLeft}%`,
                width: `${Math.max(0, bandRight - bandLeft)}%`,
                background: BAND_FILL,
                opacity: BAND_FILL_OPACITY,
                borderLeft: `1px solid ${BAND_EDGE}`,
                borderRight: `1px solid ${BAND_EDGE}`,
              }}
            />
          ) : null}
          <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${bins.length}, minmax(0, 1fr))` }}>
            {bins.map((bin, index) => (
              <div
                key={bin.id}
                data-testid={`${testIdPrefix}-bin-${index}`}
                style={props.bounded ? { position: 'relative', padding: '0 3px' } : { position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', padding: '0 3px' }}
              >
                <span
                  ref={index === 0 ? props.valueRef : undefined}
                  data-testid={`${testIdPrefix}-bin-${index}-value`}
                  style={{ ...(props.bounded ? { position: 'absolute', bottom: `${pctFor(bin.share)}%`, left: '50%', transform: 'translateX(-50%)' } as const : {}), fontSize: 11, fontVariantNumeric: 'tabular-nums', marginBottom: 2, whiteSpace: 'nowrap' }}
                >
                  {binFigure(bin, shareUnit, locale)}
                </span>
                <div
                  data-overflow={bin.isOverflow ? 'true' : undefined}
                  style={{
                    ...(props.bounded ? { position: 'absolute', bottom: 0, left: 3, right: 3 } as const : { width: '100%' }),
                    height: `${pctFor(bin.share)}%`,
                    borderRadius: '2px 2px 0 0',
                    background: bin.isOverflow
                      ? 'repeating-linear-gradient(45deg, var(--slide-chart-1) 0 3px, transparent 3px 6px)'
                      : 'var(--slide-chart-1)',
                    opacity: bin.isOverflow ? 0.6 : 1,
                  }}
                />
              </div>
            ))}
          </div>
          {medianLeft != null ? (
            <div
              data-testid={`${testIdPrefix}-median-line`}
              style={{ position: 'absolute', top: 0, bottom: 0, left: `${medianLeft}%`, borderLeft: `1.5px dashed ${INK}` }}
            />
          ) : null}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${bins.length}, minmax(0, 1fr))`, marginLeft: REPORT_TICK_COLUMN }}>
        {bins.map((bin, index) => (
          <span
            key={bin.id}
            data-testid={`${testIdPrefix}-bin-${index}-label`}
            style={{ textAlign: 'center', fontSize: 10.5, color: MUTED, padding: '0 2px', overflowWrap: 'anywhere' }}
          >
            {bin.label ?? ''}
          </span>
        ))}
      </div>
    </section>
  )
}

interface SeriesHeaderProps {
  series: DensityViewSeries
  baseText: string | null
  lowBaseText?: string
  testIdPrefix: string
}

function SeriesHeader(props: SeriesHeaderProps) {
  const { series, baseText, lowBaseText, testIdPrefix } = props

  return (
    <div data-testid={`${testIdPrefix}-header`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
      {series.label ? <span style={{ fontWeight: 600 }}>{series.label}</span> : null}
      {baseText ? <span style={{ color: MUTED }}>{baseText}</span> : null}
      {series.isThinBase && lowBaseText ? (
        <span data-testid={`${testIdPrefix}-low-base`} style={{ color: MUTED }}>
          {lowBaseText}
        </span>
      ) : null}
    </div>
  )
}
